import { useEffect, useRef, useState } from 'react';
import type { Asset, IssuedKitItem, Station } from '@hcc/types';
import { useAssetsList } from '../../hooks/useAssets';
import type { KitDraftSeed } from '../../hooks/useKitDrafts';

export interface ConfirmRow {
  asset: Asset;
  /** Null until the operator picks; both paths must be resolved to sign. */
  outcome: 'confirmed' | 'not_present' | null;
  /**
   * Identifiers seeded from the asset but editable at confirm time: the
   * imported record may be missing or wrong (no serial captured, a typo),
   * and the person is holding the item, so the desk can correct it. The
   * edited value is what gets signed for and lands in the asset register.
   */
  serial?: string;
  registration?: string;
  /** Optional, confirmed items only: last service date (YYYY-MM-DD). */
  last_service_date?: string;
  /** Optional condition photo as a base64 JPEG data URL. */
  photoDataUrl?: string;
  /** Optional free-form attributes captured at the desk (Make -> Toshiba). */
  attributes?: Record<string, string>;
}

/**
 * Resolved confirm rows to KitIssued items, carrying the outcome and (for
 * confirmed items) an optional service date. Unresolved rows are dropped.
 * Shared by the signature submit and the handover modal.
 */
export function confirmRowsToItems(rows: ConfirmRow[]): IssuedKitItem[] {
  return rows
    .filter((r) => r.outcome !== null)
    .map((r) => {
      const serial = r.serial?.trim() || undefined;
      const registration = r.registration?.trim() || undefined;
      const hasAttrs = r.attributes && Object.keys(r.attributes).length > 0;
      return {
        name: r.asset.description,
        quantity: 1,
        ...(serial ? { serial } : {}),
        ...(registration ? { registration } : {}),
        asset_id: r.asset.id,
        outcome: r.outcome as 'confirmed' | 'not_present',
        ...(r.outcome === 'confirmed' && r.last_service_date
          ? { last_service_date: r.last_service_date }
          : {}),
        ...(r.outcome === 'confirmed' && hasAttrs ? { attributes: r.attributes } : {}),
      };
    });
}

/** Condition photos from confirmed rows, ready for the append's kit_photos. */
export function confirmRowsToPhotos(
  rows: ConfirmRow[],
): { asset_id: string; photo_jpeg: string }[] {
  return rows
    .filter((r) => r.outcome === 'confirmed' && r.photoDataUrl)
    .map((r) => ({ asset_id: r.asset.id, photo_jpeg: r.photoDataUrl! }));
}

/**
 * Owns the "items to confirm" list for a TUPE transferee at one station:
 * assets pre-assigned to the person that carry confirm_on_arrival and are
 * still 'assigned' (a confirmed one flips to 'issued' and drops out; a
 * not-present one stays 'assigned' and re-seeds on a later visit).
 *
 * Seeds once from a fetch made after the drawer mounted, mirroring
 * useKitIssueItems, so a stale cache can't repopulate an item already
 * confirmed on this visit.
 *
 * A saved draft contributes only the desk's edits (outcome, corrected
 * identifiers, service date), overlaid onto the live asset list. Building
 * the rows from live data every time is what stops a draft that outlived
 * its issue from re-offering an asset the person has already signed for:
 * that asset is no longer 'assigned', so it simply isn't there to overlay.
 */
export function useKitConfirmItems(personId: string, station: Station, draft: KitDraftSeed) {
  const [rows, setRows] = useState<ConfirmRow[]>([]);
  const preAssigned = useAssetsList({ station, assignee: personId });
  // Nonce this was seeded at, held as both a ref (synchronous guard) and
  // derived state (so it invalidates in the same render a re-seed starts).
  // See the matching comment in useKitIssueItems.
  const seeded = useRef<number | null>(null);
  const [seededNonce, setSeededNonce] = useState<number | null>(null);
  const hasSeeded = seededNonce === draft.nonce;

  useEffect(() => {
    if (seeded.current === draft.nonce || !draft.ready) return;
    if (!preAssigned.isFetchedAfterMount || !preAssigned.data) return;
    const toConfirm = preAssigned.data.filter(
      (a) => a.confirm_on_arrival && a.status === 'assigned',
    );
    const draftByAssetId = new Map((draft.confirm ?? []).map((d) => [d.asset_id, d]));
    setRows(
      toConfirm.map((asset) => {
        const saved = draftByAssetId.get(asset.id);
        return {
          asset,
          outcome: saved?.outcome ?? null,
          // A saved edit wins over the asset record; that's the whole point
          // of the field being editable at the desk.
          serial: saved?.serial ?? (asset.metadata?.serial as string | undefined) ?? undefined,
          registration:
            saved?.registration ??
            (asset.metadata?.registration as string | undefined) ??
            undefined,
          ...(saved?.last_service_date ? { last_service_date: saved.last_service_date } : {}),
          ...(saved?.attributes ? { attributes: saved.attributes } : {}),
        };
      }),
    );
    seeded.current = draft.nonce;
    setSeededNonce(draft.nonce);
  }, [preAssigned.data, preAssigned.isFetchedAfterMount, draft.ready, draft.confirm, draft.nonce]);

  function setOutcome(assetId: string, outcome: 'confirmed' | 'not_present') {
    setRows((prev) =>
      prev.map((r) =>
        r.asset.id === assetId
          ? // Switching to not-present drops the confirm-only extras (date,
            // photo) but keeps identifier edits - they describe the asset.
            outcome === 'not_present'
            ? { asset: r.asset, outcome, serial: r.serial, registration: r.registration }
            : { ...r, outcome }
          : r,
      ),
    );
  }
  function setServiceDate(assetId: string, date: string) {
    setRows((prev) =>
      prev.map((r) => (r.asset.id === assetId ? { ...r, last_service_date: date || undefined } : r)),
    );
  }
  function setPhoto(assetId: string, dataUrl: string | undefined) {
    setRows((prev) =>
      prev.map((r) => (r.asset.id === assetId ? { ...r, photoDataUrl: dataUrl } : r)),
    );
  }
  function setSerial(assetId: string, value: string) {
    setRows((prev) => prev.map((r) => (r.asset.id === assetId ? { ...r, serial: value } : r)));
  }
  function setRegistration(assetId: string, value: string) {
    setRows((prev) => prev.map((r) => (r.asset.id === assetId ? { ...r, registration: value } : r)));
  }
  function setAttributes(assetId: string, attrs: Record<string, string>) {
    setRows((prev) => prev.map((r) => (r.asset.id === assetId ? { ...r, attributes: attrs } : r)));
  }

  const hasRows = rows.length > 0;
  const allResolved = rows.every((r) => r.outcome !== null);

  return {
    rows,
    seeded: hasSeeded,
    hasRows,
    allResolved,
    setOutcome,
    setServiceDate,
    setPhoto,
    setSerial,
    setRegistration,
    setAttributes,
  };
}
